import { Paginated } from '@/api'
import { IBasePagination } from '@/api/interfaces/IBasePagination'
import { CommentStatus } from '@/api/types/comment'
import useIntersect from '@/hooks/useIntersect'
import { produce } from 'immer'
import { useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import { useDialogStore } from '../dialog/FullScreenBackDrop'
import { useTopLoader } from '../@fork/NextTopLoader'

type Props<O, P = IBasePagination, T = Paginated<unknown>> = {
  initial?: T
  ignoreLoader?: boolean
  id: string
  params: P
  paginateFunc: (args: P) => Promise<T>
  enabled?: boolean
  cursorKey?: string
}

export const useInfinitePagination = <
  O,
  P extends IBasePagination = IBasePagination,
  T extends Paginated<unknown> = Paginated<unknown>,
>({
  id,
  params,
  initial,
  ignoreLoader,
  paginateFunc,
  enabled = true,
  cursorKey = 'cursorId',
}: Props<O, P, T>) => {
  const loader = useTopLoader()
  const [count, setCount] = useState(initial?.count || 0)
  const amount = params.amount

  const safeNum = (num?: number) => num || 0

  const { data, isLoading, isValidating, setSize, mutate, size } =
    useSWRInfinite(
      (index, prev) => {
        if (!enabled) {
          return
        }

        const paramsCopy = JSON.parse(JSON.stringify(params))

        if (prev && prev.cursor) {
          paramsCopy[cursorKey] = prev.cursor
        }

        return [id, paramsCopy, initial]
      },
      async (args) => {
        const result = await paginateFunc(args[1])
        return result
      },
      {
        fallbackData: initial ? [initial] : [],
        keepPreviousData: true,
        initialSize: 1,
        revalidateOnFocus: false,
        refreshInterval: 0,
        revalidateAll: false,
      },
    )

  const rows = useMemo(() => {
    return (data?.flatMap((p) => p.rows) || []) as O[]
  }, [data])

  const listBottom = useIntersect(
    () => {
      if (
        !isLoading &&
        !isValidating &&
        Number(data?.[data.length - 1]?.rows.length) >= amount &&
        rows.length < (count || 0)
      ) {
        setSize((prev) => prev + 1)
      }
    },
    { rootMargin: '400px' },
  )

  useEffect(() => {
    if (!initial && data && !isLoading && !isValidating) {
      setCount(data?.[data.length - 1]?.count || 0)
    }
  }, [size, isLoading, isValidating])

  useEffect(() => {
    if (initial && !isLoading && !isValidating) {
      setCount(initial.count || 0)
    }
  }, [initial, isLoading, isValidating])

  // REFER: loading 관련 UI는 isLoading 상태를 토대로 사용하는 곳에서 제어권장
  useEffect(() => {
    if (ignoreLoader) {
      return
    }

    if (isValidating) {
      loader.startSpin()
    } else {
      loader.stopSpin()
    }
  }, [isValidating, ignoreLoader])

  return {
    listBottom,
    rows,
    count,
    isLoading,
    setCount,
    update: async (replace: O, i: number, countAcc?: number) =>
      await mutate(
        produce(data, (draft) => {
          if (draft) {
            draft[Math.floor(i / amount)].rows[i % amount] = replace

            if (typeof countAcc === 'number') {
              setCount((prev) => safeNum(prev) + countAcc)
            }
          }
        }),
        { revalidate: false },
      ),
    unshift: async (replace: O) =>
      await mutate(
        produce(data, (draft) => {
          if (draft) {
            draft[0].rows.unshift(replace)
            setCount((prev) => safeNum(prev) + 1)
          }
        }),
        { revalidate: false },
      ),
    deleteRow: async (i: number, withoutCount?: boolean) =>
      await mutate(
        produce(data, (draft) => {
          if (draft) {
            draft[0].rows.splice(i, 1)
            if (!withoutCount) {
              setCount((prev) => safeNum(prev) - 1)
            }
          }
        }),
        { revalidate: false },
      ),
    // Comment Only
    deleteComment: async (i: number) =>
      await mutate(
        produce(data, (draft) => {
          if (draft) {
            const comment = draft[0].rows[i] as { commentStatus: CommentStatus }
            comment.commentStatus = CommentStatus.DELETED
            setCount((prev) => safeNum(prev) - 1)
          }
        }),
        { revalidate: false },
      ),
  }
}
